import React from 'react';
import Modal from '../../../shared/ui/Modal';

import {
    normalizeItemListColumnPreferences,
    resetItemListColumnOrder,
    resetItemListColumnWidths,
    ItemListColumnPreferences,
} from '../model/item-list.types';
import { useAuth } from '../../auth/AuthContext';
import { updateUserConfig } from '../../../api/account';
import ItemListColumnConfigTable from './ItemListColumnConfigTable';


interface ItemListColumnConfigModalProps {
    show: boolean;
    itemListColumnPreferences: ItemListColumnPreferences;
    onHide: () => void;
    onColumnConfigChange: (newPreferences: ItemListColumnPreferences) => void;
}

const ItemListColumnConfigModal: React.FC<ItemListColumnConfigModalProps> = (props) => {

    const auth = useAuth();
    const { show, itemListColumnPreferences, onHide, onColumnConfigChange } = props;

    const [preferences, setPreferences] = React.useState<ItemListColumnPreferences>(
        () => normalizeItemListColumnPreferences(itemListColumnPreferences),
    );
    const [isModified, setIsModified] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    // show 변경 시 상태 초기화
    React.useEffect(() => {
        if (show) {
            setPreferences(normalizeItemListColumnPreferences(itemListColumnPreferences));
            setIsModified(false);
            setErrorMessage("");
        }
    }, [show, itemListColumnPreferences]);

    const handlePreferencesChange = (newPreferences: ItemListColumnPreferences) => {
        setPreferences(newPreferences);
        setIsModified(true);
    };

    const handleOrderReset = () => {
        setPreferences(resetItemListColumnOrder(preferences));
        setIsModified(true);
    };

    const handleWidthReset = () => {
        setPreferences(resetItemListColumnWidths(preferences));
        setIsModified(true);
    };

    const handleSave = async () => {
        setIsSaving(true);
        setErrorMessage("");

        try {
            await updateUserConfig({
                payload: { item_list_column_config: preferences },
                auth: auth,
                setErrorMessage: (msg: string) => setErrorMessage(msg),
                setSuccessMessage: () => {
                    onColumnConfigChange(preferences);
                    onHide();
                }
            });
        } catch (error) {
            console.error("저장 중 오류:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setPreferences(normalizeItemListColumnPreferences(itemListColumnPreferences));
        setIsModified(false);
        setErrorMessage("");
        onHide();
    };

    return (
        <Modal show={show} onHide={handleCancel} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>컬럼 설정</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {errorMessage && (
                    <div className="alert alert-danger alert-dismissible" role="alert">
                        {errorMessage}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setErrorMessage("")}
                            aria-label="Close"
                        ></button>
                    </div>
                )}

                <div className="d-flex justify-content-between align-items-center mb-2">
                    <p className="text-muted small mb-0">드래그하여 컬럼 순서를 변경할 수 있습니다.</p>
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleWidthReset}
                            disabled={isSaving || !Object.keys(preferences.widths ?? {}).length}
                        >
                            너비초기화
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleOrderReset}
                            disabled={isSaving}
                        >
                            순서초기화
                        </button>
                    </div>
                </div>

                <ItemListColumnConfigTable
                    preferences={preferences}
                    onPreferencesChange={handlePreferencesChange}
                />
            </Modal.Body>

            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancel}
                    disabled={isSaving}
                >
                    취소
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={!isModified || isSaving}
                >
                    {isSaving ? "저장 중..." : "저장"}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default ItemListColumnConfigModal;
